import React, { memo } from 'react';
import classNames from 'classnames';

import Popup from '../helpers/Popup';
import FormGroup from '../helpers/FormGroup';
import ButtonReal from '../helpers/Button';

import BaseInput from '../helpers/Input';
import BaseTextarea from '../helpers/Textarea';
import BaseSelect from '../helpers/Select';
import BaseAutoComplete, { InputProps } from '../helpers/AutoComplete';
import uniqueId from '../../utils/uniqueId';
import { unwrap, isChanged } from '../../utils/ChangeSpy';
import PrettyError from '../decorators/PrettyError';
import DismissAlert from '../decorators/DismissAlert';
import DisplayError from '../decorators/DisplayError';
import canHasParent from '../Company/canHasParent';
import { getTickerExchange } from '../Company/getTickerExchange';
import DropDown from '../helpers/DropDown/Changable';
import { PhoneInput } from '../helpers/PhoneInput';
import BaseBoard from './BIC';
import { createIndustryErrorMessage } from '../../utils/industryTagsHelper';
import FieldError from '../helpers/FieldError';
import useRequiredFieldErrors from '../../hooks/useRequiredFieldErrors';

const Alert = () => (
  <div>
    <p>Please ensure that this address does not contain any of the following:</p>
    <ul className="pl20">
      <li>abbreviated street labels (e.g. st., Blvd., Ave., etc.)</li>
      <li>abbreviated cardinal directions (i.e. N, S, E, W)</li>
      <li>any other abbreviations, including Ste., etc.</li>
    </ul>
  </div>
);
const Input = PrettyError(DisplayError(BaseInput));
const Textarea = PrettyError(DisplayError(BaseTextarea));
const Select = PrettyError(DisplayError(BaseSelect));
const Address = DismissAlert(Textarea, Alert);
const AutoComplete = PrettyError(DisplayError(BaseAutoComplete));

const Row = ({ children }) => <div className="row media-450">{children}</div>;

const id = uniqueId();
const Board = PrettyError(DisplayError(BaseBoard));

const AddCompanyTargetPopup = props => {
  const {
    suggestDirector,
    suggestAnalyst,
    company,
    isValid,
    onChange,
    entityTypes,
    states,
    onToggleCountry,
    suggestionProps,
    onParentCompanyClick,
    onCreate,
    countryComponent,
    onBlur,
    onErrorClose,
    onClose,
    error,
    submitting,
    businessModels,
    onClick,
    tags,
    fromPositionPopup,
  } = props;

  const { errors: requiredErrors, formProps } = useRequiredFieldErrors();

  const header = 'New Target Company Profile';
  const footer = (
    <>
      <ButtonReal
        key="footerBtn"
        className="btn-primary btn-xs"
        disabled={!isValid}
        form={id}
        loading={submitting}
        type="submit"
      >
        Create
      </ButtonReal>

      {fromPositionPopup && (
        <ButtonReal key="closeBtn" className="btn-default btn-xs" onClick={onClose}>
          Close
        </ButtonReal>
      )}
    </>
  );

  const showCountry = company.get('showCountry', false);
  const countryWrapperClass = classNames('f-country col-lg-4 col-sm-4', {
    hide: !showCountry,
  });
  const parentCompany = company.get('parentCompany');
  const parentCompanyClass = canHasParent(company.get('companyType')) ? 'col-md-12' : 'hidden';

  const selectedCompany = parentCompany.get('selected');
  const numberEmployees = selectedCompany && selectedCompany.get('employees') ? selectedCompany.get('employees') : 0;
  const selectedCompanyInfo = selectedCompany && (
    <div>
      {selectedCompany.get('companyType')} ${selectedCompany.get('revenue')} / {numberEmployees} emp.{' '}
      {selectedCompany.get('city')}, {selectedCompany.get('state')}
    </div>
  );

  const Ticker = getTickerExchange(company);
  const onSubmit = submitting ? {} : { onSubmit: onCreate };

  const errorMessage = createIndustryErrorMessage(error);

  return (
    <Popup footer={footer} header={header} id="newFundRecord">
      <form id={id} onChange={onChange} {...onSubmit} {...formProps}>
        <Row>
          <FormGroup className={classNames('col-lg-6 col-xs-6', { 'has-error': requiredErrors.directorName })}>
            <InputProps className="form-control input-sm" label="Module" placeholder="Module" autoFocus required>
              {suggestDirector}
            </InputProps>
            <FieldError message={requiredErrors.directorName} />
          </FormGroup>
          <FormGroup className="col-lg-6 col-xs-6">
            <InputProps className="form-control input-sm" label="Analyst/Associate" placeholder="Analyst/Associate">
              {suggestAnalyst}
            </InputProps>
          </FormGroup>
        </Row>
        <Row>
          <FormGroup className="col-lg-6 col-xs-6">
            <Input
              canDisplayError={Boolean(requiredErrors.legalName)}
              className="input-sm"
              error={requiredErrors.legalName}
              label="Name"
              name="legalName"
              placeholder="Name"
              root={company}
              deep
              required
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-lg-6 col-xs-6">
            <Input className="input-sm" label="Abbr" name="abbrName" placeholder="Abbr" root={company} deep showLabel />
          </FormGroup>
        </Row>
        <Row>
          <FormGroup className="col-lg-6 col-xs-6">
            <Input
              className="input-sm"
              label="DBA/Alt."
              name="dbaName"
              placeholder="DBA/Alt"
              root={company}
              deep
              showLabel
            />
          </FormGroup>

          <FormGroup className="col-lg-6 col-xs-6">
            <Input
              className="input-sm"
              label="Web"
              name="web"
              onChange={onChange}
              placeholder="Web"
              root={company}
              type="web"
              deep
              showLabel
            />
          </FormGroup>
        </Row>
        <Row>
          <div className="form-group col-lg-6 col-xs-6">
            <PhoneInput
              changed={isChanged(company.get('phone'))}
              error={error.phone}
              name="phone"
              onBlur={onBlur}
              onErrorClose={onErrorClose}
              value={unwrap(company.get('phone')) || ''}
            />
          </div>
        </Row>
        <Row>
          <FormGroup className="col-lg-6 col-xs-6 target__addr-panel">
            <Address
              label="Address/Alt."
              name="address"
              placeholder="Address"
              value={company.get('address')}
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-lg-6 col-xs-6">
            <Board
              error={errorMessage}
              models={businessModels}
              name="industryCategoryIds"
              onClick={onClick}
              onErrorClose={onErrorClose}
              tags={tags}
              canDisplayError
            />
          </FormGroup>
        </Row>
        <Row>
          <FormGroup className="col-lg-4 col-xs-4">
            <Input
              className="input-sm"
              disabled={showCountry}
              label="City"
              name="city"
              placeholder="City"
              root={company}
              deep
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-lg-4 col-xs-4">
            <label className="field-label" htmlFor="dropdownMenu2">
              State
            </label>
            <DropDown
              className="form-control"
              disabled={showCountry}
              id="dropdownMenu2"
              name="state"
              onChange={onChange}
              options={states}
              root={company}
              deep
            />
          </FormGroup>

          <FormGroup className="city-index2 col-lg-4 col-xs-4">
            <label className="field-label" htmlFor="t-zip">
              Zip
            </label>
            <div className="input-group">
              <Input
                className="input-sm"
                disabled={showCountry}
                error={error.zip}
                id="t-zip"
                label="Zip"
                maxLength="30"
                name="zip"
                onBlur={onBlur}
                onFocus={() => {
                  onErrorClose();
                }}
                placeholder="Zip"
                root={company}
                deep
              />
              <span className="input-group-addon clickable" onClick={onToggleCountry} title="Country">
                <i aria-hidden="true" className="fa fa-ellipsis-h" />
              </span>
            </div>
          </FormGroup>
        </Row>
        <Row>
          <FormGroup className={countryWrapperClass}>
            <label className="field-label">Country</label>
            <div className="control-wrap" title="ONLY enter for countries outside the U.S. and Canada">
              {countryComponent}
            </div>
          </FormGroup>
        </Row>
        <Row>
          <FormGroup className="col-lg-4 col-xs-4">
            <Input
              className="input-sm"
              label="Revenue"
              name="revenue"
              placeholder="Revenue"
              root={company}
              deep
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-lg-4 col-xs-4">
            <Input
              className="input-sm"
              label="Employees"
              name="employees"
              placeholder="Employees"
              root={company}
              deep
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-lg-4 col-xs-4">
            <Select
              canDisplayError={Boolean(requiredErrors.companyType)}
              className="input-sm"
              error={requiredErrors.companyType}
              label="Company Type"
              name="companyType"
              options={entityTypes}
              root={company}
              deep
              required
              showLabel
            />
          </FormGroup>
        </Row>
        <Row>
          <div className="col-lg-8 col-xs-8">
            <Ticker company={company} exchangeName="tickerExchange" symbolName="tickerSymbol" />
          </div>
        </Row>
        <Row>
          <div className={parentCompanyClass}>
            <label className="field-label">Parent Company</label>
            <AutoComplete {...suggestionProps}>
              <span className="input-group-addon" onClick={onParentCompanyClick} title="Open parent company">
                <i aria-hidden="true" className="fa fa-external-link" />
              </span>
            </AutoComplete>
            {selectedCompanyInfo}
          </div>
        </Row>
        <Row>
          <FormGroup className="col-lg-12 col-sm-12">
            <Textarea
              label="Description"
              name="description"
              placeholder="Description"
              root={company}
              rows="4"
              deep
              showLabel
            />
          </FormGroup>
        </Row>
      </form>
    </Popup>
  );
};

export default memo(AddCompanyTargetPopup);
